import React from 'react'
import { TicketStatusCardProps } from './TicketStatusCard.types'
import { borderColorMap } from './TicketStatusCard.constants'
import TicketAmountsAndActions from './TicketAmountsAndActions'
import { useTranslation } from 'react-i18next'
import { useThemeFeatures } from '../../hooks/useThemeFeatures'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import { useThemeAssets } from '../../hooks/useThemeAssets'
const TicketStatusCard: React.FC<TicketStatusCardProps> = ({
  ticketNumber,
  betDate,
  listTitle,
  drawDate,
  amount,
  winAmount,
  status,
  onViewClick,
  canCancel,
  onCancel,
  isPrimary,
  bonus,
}) => {
  const { t } = useTranslation()
  const themeFeatures = useThemeFeatures()
  const event = useCurrentEvent()
  const { svgAssets } = useThemeAssets()

  // Check if Chinese New Year event is active
  const isChineseNewYear = event === EventType.CHINESE_NEW_YEAR
  const isSakura = event === EventType.SAKURA
  const isHalloween = event === EventType.HALLOWEEN
  const abstractBorderImage = (svgAssets as { ABSTRACT_BORDER?: string | null })
    ?.ABSTRACT_BORDER
  const webLeftImage = (svgAssets as { WEB_LEFT?: string | null })?.WEB_LEFT
  const webRightImage = (svgAssets as { WEB_RIGHT?: string | null })?.WEB_RIGHT

  // Event corner decorations (CNY/Sakura)
  const eventCornerDecorations =
    (isChineseNewYear || isSakura) && abstractBorderImage ? (
      <>
        <img
          src={abstractBorderImage}
          alt=""
          className="cny-ticket-border cny-ticket-border-tl"
        />
        <img
          src={abstractBorderImage}
          alt=""
          className="cny-ticket-border cny-ticket-border-tr"
        />
        <img
          src={abstractBorderImage}
          alt=""
          className="cny-ticket-border cny-ticket-border-bl"
        />
        <img
          src={abstractBorderImage}
          alt=""
          className="cny-ticket-border cny-ticket-border-br"
        />
      </>
    ) : null

  // Halloween web decorations (top 2 corners only)
  const halloweenWebDecorations =
    isHalloween && webLeftImage && webRightImage ? (
      <>
        <img
          src={webLeftImage}
          alt=""
          className="cny-ticket-border cny-ticket-border-tl"
        />
        <img
          src={webRightImage}
          alt=""
          className="cny-ticket-border cny-ticket-border-tr"
        />
      </>
    ) : null

  // Blue theme layout
  if (themeFeatures.ticketDetailsBlueLayout) {
    return (
      <div
        className={`w-full bg-[var(--card-bg)] text-[var(--card-title)] shadow rounded-xl px-4 py-6 sm:px-8
      flex flex-col gap-4 relative overflow-visible
      border-t-4 ${borderColorMap[status]} ${isChineseNewYear ? 'cny-ticket-card' : ''} ${isSakura ? 'sakura-ticket-card' : ''} ${isHalloween ? 'halloween-ticket-card' : ''}`}
      >
        {eventCornerDecorations}
        {halloweenWebDecorations}
        {/* Top Row: Ticket Number (left) and Bet Date (right) */}
        <div className="flex justify-between items-start w-full">
          <div className="flex flex-col gap-1">
            <div className="text-xs sm:text-sm text-[var(--card-subtext)]">
              {t('pollItem.pollNumber')}
            </div>
            <h5 className="text-lg sm:text-xl font-bold text-[var(--card-title)]">
              {ticketNumber}
            </h5>
          </div>
          <div className="flex flex-col gap-1 text-right">
            <div className="text-xs sm:text-sm text-[var(--card-subtext)]">
              {t('pollItem.betDate')}
            </div>
            <h5 className="text-sm sm:text-base font-medium text-[var(--card-title)]">
              {betDate}
            </h5>
          </div>
        </div>

        <hr className="border-t border-[var(--card-divider)]" />

        {/* Middle: List info with draw date */}
        <div className="flex flex-col gap-1">
          <div className="text-xs sm:text-sm text-[var(--card-subtext)]">
            {t('pollItem.list')}
          </div>
          <h4 className="text-base sm:text-lg font-bold text-[var(--card-title)]">
            {listTitle}
          </h4>
          {drawDate && (
            <div className="text-xs sm:text-sm text-[var(--card-subtext)]">
              {drawDate}
            </div>
          )}
        </div>

        <hr className="border-t border-[var(--card-divider)]" />

        {/* Bottom Row: Amounts and Status - gold and blue used same action component */}
        <TicketAmountsAndActions
          amount={amount}
          winAmount={winAmount}
          bonus={bonus}
          status={status}
          canCancel={canCancel}
          onCancel={onCancel}
          onViewClick={onViewClick}
          isPrimary={isPrimary}
        />
      </div>
    )
  }

  // Gold theme layout
  return (
    <div
      className={`w-full bg-[var(--card-bg)] text-[var(--card-title)] shadow rounded-xl px-4 py-6 sm:py-6 sm:px-8
      flex flex-col gap-4 sm:gap-6 relative overflow-visible
      border-t-4 ${borderColorMap[status]} ${isChineseNewYear ? 'cny-ticket-card' : ''} ${isSakura ? 'sakura-ticket-card' : ''} ${isHalloween ? 'halloween-ticket-card' : ''}`}
    >
      {eventCornerDecorations}
      {halloweenWebDecorations}
      {/* Top: List info + ticket number + bet date */}
      <div className="flex flex-col sm:flex-row w-full sm:justify-between gap-2 text-xs sm:text-sm text-[var(--card-subtext)] sm:min-w-[150px]">
        <div className="flex w-full flex-col gap-1">
          <div>{t('pollItem.list')}</div>
          <h4 className="text-base font-semibold text-[var(--card-title)]">
            {listTitle}
            {drawDate && (
              <span className="text-xs sm:text-sm text-[var(--card-subtext)] font-normal ml-2">
                ({drawDate})
              </span>
            )}
          </h4>
        </div>

        {/* Mobile: Ticket Number and Bet Date in same row */}
        <div className="flex sm:hidden justify-between w-full gap-4">
          <div className="flex flex-col gap-1">
            <div>{t('pollItem.pollNumber')}</div>
            <h5 className="font-semibold text-[var(--card-title)]">
              {ticketNumber}
            </h5>
          </div>
          <div className="flex flex-col gap-1 text-right">
            <div>{t('pollItem.betDate')}</div>
            <h5 className="font-medium text-[var(--card-title)]">{betDate}</h5>
          </div>
        </div>

        {/* Desktop: Separate columns */}
        <div className="hidden sm:flex items-start sm:items-center w-full flex-col gap-1">
          <div>{t('pollItem.pollNumber')}</div>
          <h5 className="font-semibold text-[var(--card-title)]">
            {ticketNumber}
          </h5>
        </div>
        <div className="hidden sm:flex w-full flex-col gap-1 text-left sm:text-right">
          <div>{t('pollItem.betDate')}</div>
          <h5 className="font-medium text-[var(--card-title)]">{betDate}</h5>
        </div>
      </div>
      <hr className="border-t border-[var(--card-divider)] my-2" />

      {/* Bottom: Amounts and Actions - gold and blue used same action component */}
      <TicketAmountsAndActions
        amount={amount}
        winAmount={winAmount}
        bonus={bonus}
        status={status}
        canCancel={canCancel}
        onCancel={onCancel}
        onViewClick={onViewClick}
        isPrimary={isPrimary}
      />
    </div>
  )
}

export default TicketStatusCard
